import { useState, type FormEvent } from 'react'
import {
  AleloButton,
  AleloInput,
  AleloLayout,
} from '@/components/AleloLayout'
import styles from '@/components/AleloLayout.module.css'
import { RangoAlert, RangoInputCode, RangoLink } from '@/components/rango'
import { AleloStepper } from '@/components/AleloStepper'
import * as signupFlowMarker from '@/utils/signupFlowMarker'
import * as mfaEnrollmentMarker from '@/utils/mfaEnrollmentMarker'
import { formatBrPhone } from '@/utils/phone'
import { keepEmailTogether } from '@/components/KeepTogether'
import {
  CODE_LENGTH,
  useMfaOtpManager,
  type MfaChannel as Channel,
  type MfaMode as Mode,
} from './hooks/useMfaOtpManager'
import localeJson from './locales/pt-BR.json'

type MfaCopy = {
  title: string
  description: string
  submit: string
}

type MfaEntryCopy = MfaCopy & {
  confirm?: string
  confirmTitle?: string
  confirmSubmit?: string
  resendSent: string
}

type MfaChannelCopy = {
  challenge: MfaEntryCopy
  enrollment: MfaEntryCopy
  /** Desafio que fecha a ativação (S11 depois da S12). */
  activation?: MfaEntryCopy
}

type MfaLocales = {
  codeLabel: string
  codeSupport: string
  destination: string
  phoneLabel: string
  phonePlaceholder: string
  phoneSupport: string
  invalidCode: string
  invalidPhone: string
  failed: string
  sendFailed: string
  resend: string
  resendIn: string
  email: MfaChannelCopy
  sms: MfaChannelCopy
  successTitle: string
  successEmail: string
  successSms: string
  signupFlow?: Record<Channel, Partial<Record<Mode, MfaCopy>>>
}

const locales = localeJson as MfaLocales

/** Etapa do stepper de cadastro por canal — só durante o fluxo de cadastro. */
const SIGNUP_FLOW_STEP: Record<Channel, 2 | 3> = { email: 2, sms: 3 }

const fmt = (template: string, vars: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (_, key) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )

type MfaProps = {
  channel: Channel
  mode: Mode
  /** Força o modo ativação do desafio por SMS (entrada própria no preview). */
  activation?: boolean
}

/**
 * S9–S12 — desafio/enrollment de MFA por e-mail e SMS.
 * O stepper do cadastro só aparece quando o marcador de cadastro está ativo
 * (nunca em login normal).
 */
export function MfaOtpScreen({ channel, mode, activation = false }: MfaProps) {
  const [code, setCode] = useState('')
  const [phone, setPhone] = useState('')
  const [pending, setPending] = useState(false)
  const [done, setDone] = useState(false)
  const {
    phase,
    inUniversalLogin,
    destination,
    destinationRaw,
    verifyCode,
    enrollPhone,
    resend,
    resendDisabled,
    resendSeconds,
    resendNotice,
    errorCode,
    texts,
  } = useMfaOtpManager(channel, mode)

  const inSignupFlow = signupFlowMarker.isActive() && inUniversalLogin
  // Ativação por SMS: a S12 deixa o marcador e o Auth0 abre o desafio para o
  // código. A copy de cadastro, quando ativa, continua valendo.
  const activationCopy =
    channel === 'sms' &&
    mode === 'challenge' &&
    !inSignupFlow &&
    (activation ||
      (inUniversalLogin && mfaEnrollmentMarker.isActiveFor(destinationRaw)))
      ? locales.sms.activation
      : undefined
  const signupCopy = locales.signupFlow?.[channel]?.[mode]
  const channelCopy: MfaEntryCopy = activationCopy ?? locales[channel][mode]
  const copy: MfaCopy = inSignupFlow && signupCopy ? signupCopy : channelCopy
  const isEnrollment = mode === 'enrollment'
  const phonePhase = phase === 'phone'

  // No modo ativação a copy própria vem antes do texto do Auth0, que é o
  // genérico de login e não fala em ativação.
  const title =
    activationCopy?.title ??
    (texts?.title ||
      (phase === 'code' && channelCopy.confirmTitle && !inSignupFlow
        ? channelCopy.confirmTitle
        : copy.title))
  const description = phonePhase
    ? channelCopy.description
    : (activationCopy?.description ??
      (texts?.description ||
        (phase === 'code' && channelCopy.confirm && !inSignupFlow
          ? channelCopy.confirm
          : copy.description)))
  const submitLabel = phonePhase
    ? (texts?.buttonText ?? channelCopy.submit)
    : (activationCopy?.submit ??
      (!inSignupFlow && channelCopy.confirmSubmit
        ? channelCopy.confirmSubmit
        : (texts?.buttonText ?? copy.submit)))

  const errorMessage =
    errorCode === 'INVALID_CODE'
      ? fmt(locales.invalidCode, { length: CODE_LENGTH })
      : errorCode === 'INVALID_PHONE'
        ? locales.invalidPhone
        : errorCode === 'SEND_FAILED'
          ? locales.sendFailed
          : errorCode === 'CODE_REJECTED'
            ? locales.failed
            : undefined

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (pending) return
    setPending(true)
    try {
      const result = phonePhase
        ? await enrollPhone(phone)
        : await verifyCode(code)
      if (!result.errorCode && !phonePhase) {
        // Preview local: sem UL não há navegação do Auth0 — sinaliza sucesso.
        if (!inUniversalLogin) setDone(true)
      }
    } finally {
      setPending(false)
    }
  }

  async function onResend() {
    if (resendDisabled || pending) return
    setPending(true)
    try {
      await resend()
    } finally {
      setPending(false)
    }
  }

  if (done) {
    return (
      <AleloLayout>
        <h1 className={styles.title}>{locales.successTitle}</h1>
        <RangoAlert tone="positive">
          {channel === 'sms' ? locales.successSms : locales.successEmail}
        </RangoAlert>
      </AleloLayout>
    )
  }

  return (
    <AleloLayout>
      {inSignupFlow ? <AleloStepper currentStep={SIGNUP_FLOW_STEP[channel]} /> : null}
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.subtitle}>{keepEmailTogether(description)}</p>

      {!phonePhase && destination ? (
        <p className={styles.muted}>
          {fmt(locales.destination, { destination })}
        </p>
      ) : null}

      <form className={styles.stack} onSubmit={onSubmit} noValidate>
        {phonePhase ? (
          <AleloInput
            id="phone"
            type="tel"
            required
            inputMode="tel"
            autoComplete="tel"
            label={locales.phoneLabel}
            supportText={locales.phoneSupport}
            placeholder={locales.phonePlaceholder}
            value={phone}
            onChange={(e) => setPhone(formatBrPhone(e.target.value))}
            error={errorCode === 'INVALID_PHONE' ? locales.invalidPhone : undefined}
            disabled={pending}
          />
        ) : (
          <RangoInputCode
            required
            label={locales.codeLabel}
            value={code}
            onChange={setCode}
            length={CODE_LENGTH}
            supportText={fmt(locales.codeSupport, { length: CODE_LENGTH })}
            error={
              errorCode === 'INVALID_CODE' || errorCode === 'CODE_REJECTED'
                ? errorMessage
                : undefined
            }
            disabled={pending}
            autoComplete="one-time-code"
            autoFocus
          />
        )}

        {resendNotice ? (
          <RangoAlert tone="positive">{channelCopy.resendSent}</RangoAlert>
        ) : null}

        {errorCode === 'SEND_FAILED' ? (
          <RangoAlert tone="negative">{errorMessage}</RangoAlert>
        ) : null}

        <AleloButton type="submit" disabled={pending} loading={pending}>
          {submitLabel}
        </AleloButton>

        {!phonePhase && (isEnrollment || mode === 'challenge') ? (
          <div className={styles.center}>
            <RangoLink
              onClick={() => void onResend()}
              aria-disabled={resendDisabled || pending}
              disabled={resendDisabled || pending}
            >
              {resendSeconds > 0
                ? fmt(locales.resendIn, { n: resendSeconds })
                : locales.resend}
            </RangoLink>
          </div>
        ) : null}
      </form>
    </AleloLayout>
  )
}

export function MfaEmailChallengeScreen() {
  return <MfaOtpScreen channel="email" mode="challenge" />
}

export function MfaSmsChallengeScreen() {
  return <MfaOtpScreen channel="sms" mode="challenge" />
}

/** S11 em modo ativação (o código que fecha a ativação por SMS da S12). */
export function MfaSmsChallengeActivationScreen() {
  return <MfaOtpScreen channel="sms" mode="challenge" activation />
}

export function MfaEmailEnrollmentScreen() {
  return <MfaOtpScreen channel="email" mode="enrollment" />
}

export function MfaSmsEnrollmentScreen() {
  return <MfaOtpScreen channel="sms" mode="enrollment" />
}

export default MfaEmailChallengeScreen
